import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { billing } from '@repletoryreach/sdk-api';
import { rowsToCsv, downloadText } from '@repletoryreach/sdk-utils/file';
import { Stack, Card, Group, Text, Button, SimpleGrid, Badge, Progress } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, MetricCard, EmptyState, DataTable, Tabs, type DataTableColumn } from '@repletoryreach/sdk-ui/data-display';
import { Select, SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { useMetaBilling } from '../../../lib/meta-billing';
import { BarChart } from '@repletoryreach/sdk-ui/charts';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { formatCount, formatDate, formatDateTime } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useCredits, formatCreditAmount } from '../../../lib/credits';
import { useRates } from '../../../data/useRates';
import { useT } from '../../../i18n/I18nProvider';
import { UsageEntriesDrawer, type UsageEntriesTarget } from '../components/UsageEntriesDrawer';
import { maskMsisdn, sourceKindLabel, categoryLabel, unitLabel } from '../components/usage-labels';
import '../billing.css';
import { HelpButton } from '../../../app/guides/HelpButton';

/** Periods people ask for. `month` is month-to-date and carries the projection. */
const PERIODS = ['month', '7d', '30d', '90d', 'previous_month'] as const;
type Period = (typeof PERIODS)[number];

function periodRange(period: Period): { from?: string; to?: string } {
  const now = new Date();
  if (period === 'month') return {};
  if (period === 'previous_month') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1) - 1);
    return { from: start.toISOString(), to: end.toISOString() };
  }
  const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
  return { from: new Date(Date.now() - days * 86_400_000).toISOString() };
}

const CATEGORY_COLOURS: Record<string, string> = {
  whatsapp: 'var(--rr-color-success, #16a34a)',
  ai: 'var(--rr-color-primary, #0d9488)',
  knowledge: '#7c3aed',
  notifications: '#d97706',
  media: '#0284c7',
  web: '#db2777',
  meta: '#2563eb',
  voice: '#ea580c',
  other: 'var(--rr-color-text-muted, #94a3b8)'
};

export default function BillingUsagePage() {
  const t = useT();
  const credits = useCredits();
  const [params, setParams] = useSearchParams();
  const period = (PERIODS as readonly string[]).includes(params.get('period') ?? '') ? (params.get('period') as Period) : 'month';
  const setPeriod = (next: string) => setParams((prev) => {
    const q = new URLSearchParams(prev);
    if (next === 'month') q.delete('period'); else q.set('period', next);
    return q;
  }, { replace: true });

  const range = useMemo(() => periodRange(period), [period]);
  const summaryQ = useQuery({
    queryKey: ['usage', 'summary', period],
    queryFn: () => billing.getUsageSummary(range)
  });
  // A number on the business's own WhatsApp account (5 October 2026): its messages are billed by Meta, not in credits.
  const ownAccount = useMetaBilling().hasOwnAccount;
  const ratesQ = useRates();

  const s = summaryQ.data?.data;
  const currency = s?.currency ?? 'ZAR';
  const [drawer, setDrawer] = useState<UsageEntriesTarget | null>(null);
  const [tab, setTab] = useState('activity');

  /*
   * Prices follow the plan (21 September 2026). `rate_minor` is this
   * organisation's own price; `list_rate_minor` is the pay-as-you-go price
   * beside it, and `plan_rates_minor` the price on every plan on sale. With no
   * paid plan the two are equal, nothing is struck through and the tab reads
   * as it always did. The comparison is offered only when the price list names
   * more than one plan.
   */
  const [ratesView, setRatesView] = useState<'mine' | 'compare'>('mine');
  const rates = useMemo(() => (ratesQ.data?.data ?? []) as Indexed<billing.TenantRate>[], [ratesQ.data]);
  const ratesMeta = ratesQ.data?.meta;
  const planName = ratesMeta?.plan_name ?? null;
  const discounted = !!planName && rates.some((r) => (r.list_rate_minor ?? r.rate_minor) > r.rate_minor);
  const comparePlans = ratesMeta?.plans ?? [];
  const canCompare = comparePlans.length > 1 && rates.some((r) => r.plan_rates_minor);
  const comparing = canCompare && ratesView === 'compare';

  // What is priced and per what: the same two columns lead both views.
  const rateItemColumns: DataTableColumn<Indexed<billing.TenantRate>>[] = [
    {
      accessor: 'name', title: t('billing.usage.columns.item'),
      render: (r) => (
        <Stack gap={0}>
          <Text role="bodySmall">{r.name}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{categoryLabel(t, r.category)} · <code>{r.sku_code}</code></Text>
        </Stack>
      )
    },
    { accessor: 'unit', title: t('billing.usage.columns.per'), render: (r) => unitLabel(t, r.unit, 1) }
  ];

  const daily = useMemo(() => (s?.daily ?? []).map((d) => ({
    date: d.date,
    ...Object.fromEntries(Object.entries(d.by_category ?? {}).map(([k, v]) => [k, v / 100]))
  })), [s]);
  const categoriesInSeries = useMemo(() => {
    const set = new Set<string>();
    for (const d of s?.daily ?? []) for (const k of Object.keys(d.by_category ?? {})) set.add(k);
    return Array.from(set);
  }, [s]);

  const spent = s?.spent_minor ?? 0;
  const maxCategory = Math.max(1, ...(s?.by_category ?? []).map((c) => c.amount_minor));

  function exportCsv() {
    if (!s) return;
    const csv = rowsToCsv(s.by_sku.map((row) => ({
      sku: row.sku_code, name: row.name, category: row.category, unit: row.unit, units: row.units,
      rate_minor: row.rate_minor ?? '', amount_minor: row.amount_minor, entries: row.entries, currency
    })));
    downloadText(csv, `usage-${period}-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv');
    notifySuccess(t('billing.usage.exported'), t('nav.billing'));
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('billing.usage.title')}
        description={t('billing.usage.description')}
        withDivider={false}
        actions={
          <Group gap="sm">
            <HelpButton guide="account" />
            <Select
              aria-label={t('billing.usage.period')}
              value={period}
              onChange={(e) => setPeriod(e.currentTarget.value)}
              data={PERIODS.map((p) => ({ value: p, label: t(`billing.usage.period.${p}`) }))}
            />
            <Button size="sm" variant="default" onClick={exportCsv} disabled={!s || s.by_sku.length === 0}>{t('action.export')}</Button>
          </Group>
        }
      />

      {summaryQ.error && <Alert color="error" role="alert">{t('billing.usage.loadError')}</Alert>}

      {s && s.state !== 'ok' && (
        <Alert color={s.state === 'empty' ? 'error' : 'warning'} title={s.state === 'empty' ? t('billing.empty.title') : t('billing.lowBalance')}>
          {t('billing.usage.stateHint', { balance: credits(s.balance_minor, currency), threshold: credits(s.low_balance_minor, currency) })}{' '}
          <Link to="/billing/topup">{t('billing.topUpNow')}</Link>
          {' · '}
          <Link to="/billing/alerts">{t('billing.usage.manageAlerts')}</Link>
        </Alert>
      )}

      {ownAccount && (
        <Text role="bodySmall" c="var(--rr-color-text-muted)" data-testid="own-account-spend-note">{t('billing.usage.ownAccountNote')}</Text>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <MetricCard
          label={t('billing.usage.kpi.spent')}
          value={s ? credits(spent, currency) : '–'}
          hint={s ? t('billing.usage.kpi.spentHint', { count: formatCount(s.entries) }) : undefined}
          loading={summaryQ.isPending}
        />
        <MetricCard
          label={t('billing.usage.kpi.perDay')}
          value={s ? credits(s.avg_daily_spend_minor, currency) : '–'}
          hint={s?.projected_month_end_minor != null ? t('billing.usage.kpi.projected', { amount: credits(s.projected_month_end_minor, currency) }) : undefined}
          loading={summaryQ.isPending}
        />
        <MetricCard
          label={t('billing.usage.kpi.runway')}
          value={s ? (s.runway_days == null ? t('billing.usage.kpi.runwayNone') : t('billing.usage.kpi.runwayDays', { count: formatCount(Number(s.runway_days)) })) : '–'}
          hint={t('billing.usage.kpi.runwayHint')}
          loading={summaryQ.isPending}
        />
        <div className="rr-billing__metric">
          <MetricCard
            label={t('billing.walletBalance')}
            value={s ? credits(s.balance_minor, currency) : '–'}
            hint={s ? t('billing.usage.kpi.lowLine', { amount: credits(s.low_balance_minor, currency) }) : undefined}
            loading={summaryQ.isPending}
          />
          <Link to="/billing/topup" className="rr-billing__link">{t('billing.topUp')}</Link>
        </div>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Card withBorder>
          <Stack gap="sm">
            <Text role="body" fw={600}>{t('billing.usage.daily.title')}</Text>
            {daily.length > 1 ? (
              <BarChart
                data={daily}
                dataKey="date"
                stacked
                h={240}
                series={categoriesInSeries.map((c) => ({ name: c, label: categoryLabel(t, c), color: CATEGORY_COLOURS[c] ?? CATEGORY_COLOURS.other }))}
                valueFormatter={(v) => credits(Math.round(v * 100), currency)}
                labelFormatter={(v) => formatDate(String(v))}
              />
            ) : (
              <EmptyState title={t('billing.usage.daily.empty')} description={t('billing.usage.daily.emptyHint')} />
            )}
          </Stack>
        </Card>

        <Card withBorder>
          <Stack gap="sm">
            <Text role="body" fw={600}>{t('billing.usage.byCategory.title')}</Text>
            {(s?.by_category ?? []).length === 0 && !summaryQ.isPending && (
              <EmptyState title={t('billing.usage.noSpend')} description={t('billing.usage.noSpendHint')} />
            )}
            {(s?.by_category ?? []).map((c) => (
              <Stack key={c.category} gap={4}>
                <Group justify="space-between" wrap="nowrap">
                  <Group gap={8} wrap="nowrap">
                    <span aria-hidden style={{ width: 10, height: 10, borderRadius: 999, background: CATEGORY_COLOURS[c.category] ?? CATEGORY_COLOURS.other }} />
                    <Text role="bodySmall">{categoryLabel(t, c.category)}</Text>
                    <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.usage.entriesCount', { count: formatCount(c.entries) })}</Text>
                  </Group>
                  <Text role="bodySmall" fw={600}>{credits(c.amount_minor, currency)}</Text>
                </Group>
                <Progress value={c.amount_minor} max={maxCategory} size="sm" aria-label={categoryLabel(t, c.category)} />
              </Stack>
            ))}
            {spent > 0 && (
              <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.usage.byCategory.hint')}</Text>
            )}
          </Stack>
        </Card>
      </SimpleGrid>

      <Card withBorder noPadding data-guide="usage-tabs">
        <Tabs value={tab} onChange={setTab}>
          <Tabs.List>
            <Tabs.Tab value="activity">{t('billing.usage.tabs.activity')}</Tabs.Tab>
            <Tabs.Tab value="items">{t('billing.usage.tabs.items')}</Tabs.Tab>
            <Tabs.Tab value="broadcasts">{t('billing.usage.tabs.broadcasts')}</Tabs.Tab>
            <Tabs.Tab value="contacts">{t('billing.usage.tabs.contacts')}</Tabs.Tab>
            <Tabs.Tab value="flows">{t('billing.usage.tabs.flows')}</Tabs.Tab>
            <Tabs.Tab value="rates">{t('billing.usage.tabs.rates')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="activity">
            <DataTable<Indexed<billing.UsageSummary['by_source_kind'][number]>>
              records={(s?.by_source_kind ?? []) as Indexed<billing.UsageSummary['by_source_kind'][number]>[]}
              loading={summaryQ.isPending}
              idAccessor="source_kind"
              emptyState={t('billing.usage.noSpend')}
              columns={[
                { accessor: 'source_kind', title: t('billing.usage.columns.activity'), render: (r) => <Badge size="sm" variant="light">{sourceKindLabel(t, r.source_kind)}</Badge> },
                { accessor: 'entries', title: t('billing.usage.columns.entries'), textAlign: 'right', render: (r) => formatCount(r.entries) },
                { accessor: 'amount_minor', title: t('common.amount'), textAlign: 'right', render: (r) => <Text role="bodySmall" fw={600}>{credits(r.amount_minor, currency)}</Text> },
                {
                  accessor: 'share', title: t('billing.usage.columns.share'), textAlign: 'right',
                  render: (r) => `${spent > 0 ? Math.round((r.amount_minor / spent) * 100) : 0}%`
                }
              ]}
            />
          </Tabs.Panel>

          <Tabs.Panel value="items">
            <DataTable<Indexed<billing.UsageSku>>
              records={(s?.by_sku ?? []) as Indexed<billing.UsageSku>[]}
              loading={summaryQ.isPending}
              idAccessor="sku_code"
              emptyState={t('billing.usage.noSpend')}
              columns={[
                {
                  accessor: 'name', title: t('billing.usage.columns.item'),
                  render: (r) => (
                    <Stack gap={0}>
                      <Text role="bodySmall">{r.name}</Text>
                      <Text role="caption" c="var(--rr-color-text-muted)">{categoryLabel(t, r.category)} · <code>{r.sku_code}</code></Text>
                    </Stack>
                  )
                },
                { accessor: 'units', title: t('billing.usage.columns.units'), textAlign: 'right', render: (r) => `${formatUnits(r.units)} ${unitLabel(t, r.unit, r.units)}` },
                { accessor: 'rate_minor', title: t('billing.usage.columns.rate'), textAlign: 'right', render: (r) => r.rate_minor == null ? '–' : t('billing.usage.ratePer', { amount: credits(r.rate_minor, currency), unit: unitLabel(t, r.unit, 1) }) },
                { accessor: 'amount_minor', title: t('common.amount'), textAlign: 'right', render: (r) => <Text role="bodySmall" fw={600}>{credits(r.amount_minor, currency)}</Text> },
                {
                  accessor: 'actions', title: t('common.actions'), textAlign: 'right',
                  render: (r) => <Button size="xs" variant="subtle" onClick={() => setDrawer({ kind: 'sku', id: r.sku_code, label: r.name })}>{t('billing.usage.viewEntries')}</Button>
                }
              ]}
            />
          </Tabs.Panel>

          <Tabs.Panel value="broadcasts">
            <DataTable<Indexed<billing.UsageSummary['top_broadcasts'][number]>>
              records={(s?.top_broadcasts ?? []) as Indexed<billing.UsageSummary['top_broadcasts'][number]>[]}
              loading={summaryQ.isPending}
              idAccessor="broadcast_id"
              emptyState={t('billing.usage.noBroadcasts')}
              columns={[
                { accessor: 'broadcast_id', title: t('nav.broadcasts'), render: (r) => <Link to={`/broadcasts/${r.broadcast_id}`}>{t('billing.usage.broadcastLink', { id: r.broadcast_id.slice(0, 8) })}</Link> },
                { accessor: 'entries', title: t('billing.usage.columns.messages'), textAlign: 'right', render: (r) => formatCount(r.entries) },
                { accessor: 'last_at', title: t('billing.usage.columns.lastActivity'), render: (r) => formatDateTime(r.last_at) },
                { accessor: 'amount_minor', title: t('common.amount'), textAlign: 'right', render: (r) => <Text role="bodySmall" fw={600}>{credits(r.amount_minor, currency)}</Text> },
                {
                  accessor: 'actions', title: t('common.actions'), textAlign: 'right',
                  render: (r) => <Button size="xs" variant="subtle" onClick={() => setDrawer({ kind: 'broadcast', id: r.broadcast_id, label: t('billing.usage.broadcastLink', { id: r.broadcast_id.slice(0, 8) }) })}>{t('billing.usage.viewEntries')}</Button>
                }
              ]}
            />
          </Tabs.Panel>

          <Tabs.Panel value="contacts">
            <DataTable<Indexed<billing.UsageSummary['top_contacts'][number]>>
              records={(s?.top_contacts ?? []) as Indexed<billing.UsageSummary['top_contacts'][number]>[]}
              loading={summaryQ.isPending}
              idAccessor="contact_msisdn"
              emptyState={t('billing.usage.noContacts')}
              columns={[
                { accessor: 'contact_msisdn', title: t('nav.contacts'), render: (r) => <Link to={`/inbox?q=${encodeURIComponent(r.contact_msisdn)}`}>{maskMsisdn(r.contact_msisdn)}</Link> },
                { accessor: 'entries', title: t('billing.usage.columns.entries'), textAlign: 'right', render: (r) => formatCount(r.entries) },
                { accessor: 'last_at', title: t('billing.usage.columns.lastActivity'), render: (r) => formatDateTime(r.last_at) },
                { accessor: 'amount_minor', title: t('common.amount'), textAlign: 'right', render: (r) => <Text role="bodySmall" fw={600}>{credits(r.amount_minor, currency)}</Text> },
                {
                  accessor: 'actions', title: t('common.actions'), textAlign: 'right',
                  render: (r) => <Button size="xs" variant="subtle" onClick={() => setDrawer({ kind: 'contact', id: r.contact_msisdn, label: maskMsisdn(r.contact_msisdn) })}>{t('billing.usage.viewEntries')}</Button>
                }
              ]}
            />
          </Tabs.Panel>

          <Tabs.Panel value="flows">
            <DataTable<Indexed<billing.UsageSummary['top_flows'][number]>>
              records={(s?.top_flows ?? []) as Indexed<billing.UsageSummary['top_flows'][number]>[]}
              loading={summaryQ.isPending}
              idAccessor="flow_id"
              emptyState={t('billing.usage.noFlows')}
              columns={[
                { accessor: 'flow_id', title: t('nav.flows'), render: (r) => <Link to={`/flows/${r.flow_id}/builder`}>{t('billing.usage.flowLink', { id: r.flow_id.slice(0, 8) })}</Link> },
                { accessor: 'entries', title: t('billing.usage.columns.entries'), textAlign: 'right', render: (r) => formatCount(r.entries) },
                { accessor: 'last_at', title: t('billing.usage.columns.lastActivity'), render: (r) => formatDateTime(r.last_at) },
                { accessor: 'amount_minor', title: t('common.amount'), textAlign: 'right', render: (r) => <Text role="bodySmall" fw={600}>{credits(r.amount_minor, currency)}</Text> },
                {
                  accessor: 'actions', title: t('common.actions'), textAlign: 'right',
                  render: (r) => <Button size="xs" variant="subtle" onClick={() => setDrawer({ kind: 'flow', id: r.flow_id, label: t('billing.usage.flowLink', { id: r.flow_id.slice(0, 8) }) })}>{t('billing.usage.viewEntries')}</Button>
                }
              ]}
            />
          </Tabs.Panel>

          <Tabs.Panel value="rates">
            <Stack gap="xs" p="md">
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.usage.rates.hint', { currency: ratesQ.data?.meta.currency ?? currency })}</Text>
              {ratesQ.data && (
                <Text role="bodySmall" fw={600}>
                  {ratesQ.data.meta.vat_applies
                    ? t('billing.usage.rates.vatOn', { pct: ratesQ.data.meta.vat_percent, label: ratesQ.data.meta.vat_label, number: ratesQ.data.meta.vat_registration_number ?? '' })
                    : t('billing.usage.rates.vatOff')}
                </Text>
              )}
              {discounted && !comparing && (
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.usage.rates.planNote', { plan: planName ?? '' })}</Text>
              )}
              {canCompare && (
                <Group justify="space-between" align="center" wrap="wrap" gap="sm">
                  <div role="group" aria-label={t('billing.usage.rates.view')}>
                    <SegmentedControl
                      size="xs"
                      value={ratesView}
                      onChange={(v) => setRatesView(v === 'compare' ? 'compare' : 'mine')}
                      data={[
                        { value: 'mine', label: t('billing.usage.rates.view.mine') },
                        { value: 'compare', label: t('billing.usage.rates.view.compare') }
                      ]}
                    />
                  </div>
                  <Link to="/billing" className="rr-billing__link">{t('billing.usage.rates.changePlan')}</Link>
                </Group>
              )}
              {comparing && (
                <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.usage.rates.compareHint')}</Text>
              )}
            </Stack>
            {comparing ? (
              // One column per plan on sale, this organisation's own marked in
              // words and tinted. Five plans do not fit a phone, so the table
              // scrolls sideways inside its own container and the page stays put.
              <div className="rr-billing__compare">
                <DataTable<Indexed<billing.TenantRate>>
                  records={rates}
                  idAccessor="sku_code"
                  emptyState={t('billing.usage.rates.empty')}
                  columns={[
                    ...rateItemColumns,
                    ...comparePlans.map((plan): DataTableColumn<Indexed<billing.TenantRate>> => {
                      const own = plan.code === ratesMeta?.plan_code;
                      return {
                        accessor: `plan:${plan.code}`,
                        title: own
                          ? <span className="rr-billing__compare-own">{plan.name} <Badge size="xs" variant="light">{t('billing.usage.rates.yourPlan')}</Badge></span>
                          : plan.name,
                        textAlign: 'right',
                        render: (r) => {
                          const minor = r.plan_rates_minor?.[plan.code];
                          if (minor === undefined) return '–';
                          return own ? <span className="rr-billing__compare-own">{formatCreditAmount(minor)}</span> : formatCreditAmount(minor);
                        }
                      };
                    })
                  ]}
                />
              </div>
            ) : (
              <DataTable<Indexed<billing.TenantRate>>
                records={rates}
                loading={ratesQ.isPending}
                idAccessor="sku_code"
                emptyState={t('billing.usage.rates.empty')}
                columns={[
                  ...rateItemColumns,
                  {
                    accessor: 'rate_minor',
                    // On a paid plan the column is headed by whose price it is.
                    title: discounted ? t('billing.usage.rates.yourPlanPrice', { plan: planName ?? '' }) : t('billing.usage.columns.rate'),
                    textAlign: 'right',
                    render: (r) => {
                      const list = r.list_rate_minor ?? r.rate_minor;
                      return (
                        <Group gap={6} justify="flex-end" wrap="nowrap">
                          {list > r.rate_minor && (
                            // Struck through for the eye; the words are for a screen reader.
                            <Text role="caption" span c="var(--rr-color-text-muted)" className="rr-billing__was">
                              <span className="rr-visually-hidden">{t('billing.usage.rates.payg')} </span>
                              {credits(list, r.currency)}
                            </Text>
                          )}
                          <Text role="bodySmall" fw={600}>{credits(r.rate_minor, r.currency)}</Text>
                          {r.converted && <Badge size="xs" variant="light">{t('billing.usage.rates.converted')}</Badge>}
                        </Group>
                      );
                    }
                  }
                ]}
              />
            )}
          </Tabs.Panel>
        </Tabs>
      </Card>

      <UsageEntriesDrawer target={drawer} currency={currency} onClose={() => setDrawer(null)} />
    </Stack>
  );
}

function formatUnits(units: number): string {
  return Number.isInteger(units) ? formatCount(units) : units.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
